<template>
  <div class="theme-switcher">
    <button
      @click="toggleDropdown"
      class="theme-switcher__button"
      :aria-label="`Aktuelles Theme: ${displayName}`"
      aria-haspopup="true"
      :aria-expanded="isOpen"
    >
      <svg
        v-if="isLightMode"
        class="theme-icon"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <circle cx="12" cy="12" r="5"></circle>
        <line x1="12" y1="1" x2="12" y2="3"></line>
        <line x1="12" y1="21" x2="12" y2="23"></line>
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
        <line x1="1" y1="12" x2="3" y2="12"></line>
        <line x1="21" y1="12" x2="23" y2="12"></line>
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
      </svg>
      <svg
        v-else
        class="theme-icon"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
      </svg>
      <span class="theme-name">{{ displayName }}</span>
      <svg
        class="chevron-icon"
        :class="{ 'chevron-icon--open': isOpen }"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    </button>
    
    <div v-if="isOpen" class="theme-switcher__dropdown" @click.stop>
        <div class="theme-dropdown-section">
          <div class="theme-dropdown-title">Design</div>
          
          <button
            v-for="style in themeStyles"
            :key="style"
            @click="selectStyle(style)"
            class="theme-option"
            :class="{ 'theme-option--active': currentStyle === style }"
          >
            <div 
              class="style-color-preview" 
              :style="{ backgroundColor: getStylePreviewColor(style) }"
            ></div>
            <span>{{ getStyleDisplayName(style) }}</span>
            <svg
              v-if="currentStyle === style"
              class="check-icon"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </button>
        </div>
        
        <div class="theme-option-divider"></div>
        
        <div class="theme-dropdown-section">
          <div class="theme-dropdown-title">Modus</div>
          
          <button
            v-for="mode in themeModes"
            :key="mode"
            @click="selectMode(mode)"
            class="theme-option"
            :class="{ 'theme-option--active': currentMode === mode }"
          >
            <svg
              v-if="mode === 'light'"
              class="theme-icon"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="5"></circle>
              <line x1="12" y1="1" x2="12" y2="3"></line>
              <line x1="12" y1="21" x2="12" y2="23"></line>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
              <line x1="1" y1="12" x2="3" y2="12"></line>
              <line x1="21" y1="12" x2="23" y2="12"></line>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
            </svg>
            <svg
              v-else-if="mode === 'dark'"
              class="theme-icon"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
            </svg>
            <span>{{ mode === 'light' ? 'Hell' : 'Dunkel' }}</span>
            <svg
              v-if="currentMode === mode"
              class="check-icon"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </button>
          

        </div>
      </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useTheme } from '@/composables/useTheme'
import { themeGroups, type ThemeName, type ThemeStyle, type ThemeMode } from '@/assets/themes'

const { currentThemeName, setTheme } = useTheme()

const isOpen = ref(false)

// Theme-Stil und Modus aus dem aktuellen Theme ableiten
const currentStyle = ref<ThemeStyle>('professional')
const currentMode = ref<ThemeMode>('light')

// Theme-Modi und -Stile aus der Theme-Konfiguration
const themeStyles = themeGroups.styles
const themeModes = themeGroups.modes

// Prüft, ob das aktuelle Theme ein Light-Mode-Theme ist
const isLightMode = computed(() => {
  return !currentThemeName.value.toLowerCase().includes('dark')
})

// Anzeigename für das aktuelle Theme
const displayName = computed(() => {
  const styleName = getStyleDisplayName(currentStyle.value)
  const modeName = currentMode.value === 'light' ? 'Hell' : 'Dunkel'
  return `${styleName} (${modeName})`
})

// Dropdown öffnen/schließen
const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}

// Theme-Stil wählen
const selectStyle = (style: ThemeStyle) => {
  currentStyle.value = style
  
  // Theme entsprechend dem gewählten Stil und Modus setzen
  const themeName = getThemeNameFromStyleAndMode(style, currentMode.value)
  setTheme(themeName)
  
  isOpen.value = false
}

// Theme-Modus wählen
const selectMode = (mode: ThemeMode) => {
  currentMode.value = mode
  
  // Theme entsprechend dem gewählten Stil und Modus setzen
  const themeName = getThemeNameFromStyleAndMode(currentStyle.value, mode)
  setTheme(themeName)
  
  isOpen.value = false
}



// Hilfsfunktion zum Ermitteln des Theme-Names aus Stil und Modus
const getThemeNameFromStyleAndMode = (style: ThemeStyle, mode: ThemeMode): ThemeName => {
  if (mode === 'dark') {
    return `${style}Dark` as ThemeName
  }
  return style as ThemeName
}

// Hilfsfunktion zum Anzeigen von benutzerfreundlichen Namen für die Theme-Stile
const getStyleDisplayName = (style: ThemeStyle): string => {
  const styleNames: Record<ThemeStyle, string> = {
    professional: 'Standard',
    warm: 'Warm',
    modern: 'Modern'
  }
  return styleNames[style] || style
}

// Hilfsfunktion zum Anzeigen einer Vorschaufarbe für den Theme-Stil
const getStylePreviewColor = (style: ThemeStyle): string => {
  const previewColors: Record<ThemeStyle, string> = {
    professional: '#2185d0', // Blau für Professionell
    warm: '#e6b86b',         // Gelb/Orange für Warm
    modern: '#7b9d7c'        // Grün für Modern
  }
  return previewColors[style] || '#cccccc'
}

// Dropdown schließen, wenn außerhalb geklickt wird
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest('.theme-switcher')) {
    isOpen.value = false
  }
}

// Aktuelles Theme beim Laden analysieren
const parseCurrentTheme = () => {
  const name = currentThemeName.value
  
  // Grundlegende Standard-Fallback-Werte setzen
  currentStyle.value = 'professional'
  currentMode.value = 'light'
  
  // Theme-Name analysieren
  if (name.includes('Dark')) {
    currentMode.value = 'dark'
    const baseName = name.replace('Dark', '') as ThemeStyle
    if (themeGroups.styles.includes(baseName as any)) {
      currentStyle.value = baseName
    }
  } else if (themeGroups.styles.includes(name as any)) {
    currentStyle.value = name as ThemeStyle
    currentMode.value = 'light'
  } else if (name === 'dark') {
    currentMode.value = 'dark'
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  parseCurrentTheme()
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.theme-switcher {
  position: relative;
}

.theme-switcher__button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  color: var(--color-text);
  cursor: pointer;
  transition: var(--transition-fast);
}

.theme-switcher__button:hover {
  background-color: var(--color-hover);
}

.theme-name {
  display: none;
  font-size: 0.875rem;
  white-space: nowrap;
}

@media (min-width: 768px) {
  .theme-name {
    display: inline;
  }
}

.theme-icon {
  width: 20px;
  height: 20px;
}

.chevron-icon {
  width: 16px;
  height: 16px;
  transition: transform var(--transition-fast);
}

.chevron-icon--open {
  transform: rotate(180deg);
}

.theme-switcher__dropdown {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  min-width: 240px;
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-md);
  overflow: hidden;
  z-index: 1000;
}

.theme-dropdown-section {
  padding: 0.5rem 0;
}

.theme-dropdown-title {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-textLight);
  padding: 0 1rem 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.theme-option {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  color: var(--color-text);
  text-align: left;
  cursor: pointer;
  transition: var(--transition-fast);
}

.theme-option:hover {
  background-color: var(--color-hover);
}

.theme-option--active {
  color: var(--color-primary);
  font-weight: 500;
}

.style-color-preview {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid var(--color-border);
}

.theme-option-divider {
  height: 1px;
  background-color: var(--color-border);
  margin: 0 0.75rem;
}

.check-icon {
  margin-left: auto;
}

/* Dropdown Animation */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.dropdown-enter-to,
.dropdown-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
